import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { prs as prsApi, auth } from "@/lib/api";
import { toast } from "sonner";
import KpiCard from "@/components/KpiCard";
import FilterBar from "@/components/FilterBar";
import PRTable from "@/components/PRTable";
import PRDetailDrawer from "@/components/PRDetailDrawer";
import SavingsBanner from "@/components/SavingsBanner";
import PrSearchBox from "@/components/PrSearchBox";
import UserMenu from "@/components/UserMenu";
import MirrorFreshness from "@/components/MirrorFreshness";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileText, TrendingUp, Boxes, ShieldCheck, AlertOctagon, RefreshCw, Sparkles, CheckCircle2, XCircle, PauseCircle, Settings, Stamp, BadgeCheck, Pause } from "lucide-react";
import { useNavigate } from "react-router-dom";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import PendingBadge from "@/components/PendingBadge";

export default function Dashboard() {
  // Parse once on mount — auth.user() returns a freshly-parsed object every
  // call (JSON.parse from localStorage), so reading it on every render would
  // give a new reference each time, breaking the params useMemo dependency
  // chain below and triggering an effect re-fetch loop.
  const user = useMemo(() => auth.user(), []);
  const nav = useNavigate();
  const role = user?.role || "Approver";
  // What modes can this user access?
  const canAuthorise = role === "Authoriser" || role === "Both" || role === "Admin";
  const canApprove = role === "Approver" || role === "Both" || role === "Admin";
  // Default mode: Authorise (L1) is earlier in the workflow, so users with both roles
  // land there first. Pure Approvers default to "approve".
  const initialMode = canAuthorise ? "authorise" : "approve";
  const [mode, setMode] = useState(initialMode);
  const [filters, setFilters] = useState({
    purc_type: "All",
    vdate: "",
    supplier: "",
    ai_action: "All",
    approval: "Open",
    approver: "__me__",
  });
  const [rows, setRows] = useState([]);
  const [syncInfo, setSyncInfo] = useState(null);

  const [approvers, setApprovers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [focus, setFocus] = useState("all"); // all | price_inc | cheaper | approve | hold
  const [searchParams, setSearchParams] = useSearchParams();

  // Apply Fastpath deep-link params on mount / change
  useEffect(() => {
    const openP = searchParams.get("openPR");
    const sup = searchParams.get("supplier");
    const appr = searchParams.get("approver");
    const sav = searchParams.get("savings");
    let consumed = false;
    if (openP) { setOpenId(Number(openP)); setDrawerOpen(true); consumed = true; }
    if (sup) { setFilters((f) => ({ ...f, supplier: sup })); consumed = true; }
    if (appr) { setFilters((f) => ({ ...f, approver: appr })); consumed = true; }
    if (sav) { /* SavingsBanner reads its own state; just keep param for visibility */ }
    if (consumed) {
      // Strip after-consuming so reload doesn't re-trigger.
      const next = new URLSearchParams(searchParams);
      ["openPR", "supplier", "approver"].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
  }, []);

  useEffect(() => {
    api.get("/approvers").then((r) => setApprovers(r.data || [])).catch(() => {});
  }, []);

  // Server-side params — only those that change the base result set.
  // purc_type/vdate/supplier/ai_action are now filtered CLIENT-SIDE (see
  // filteredRows below) so typing in those inputs is instant with no server
  // round-trip. Matches the "load-once + client-side filter" pattern already
  // in use on Pipeline.jsx.
  const params = useMemo(() => {
    const p = { mode };
    if (filters.approval) p.approval = filters.approval;
    if (filters.approver && filters.approver !== "__me__") {
      if (filters.approver !== "all") p.approver = filters.approver;
      else p.approver = "__all__";
    }
    // 'held' mode always means "MY holds" — force approver to current user emp_no.
    if (mode === "held") p.approver = user?.emp_no || "__me__";
    return p;
  }, [filters.approval, filters.approver, mode, user]);

  const load = useCallback(async (opts = {}) => {
    setBusy(true);
    try {
      const fresh = opts.fresh === true;
      const reqParams = fresh ? { ...params, fresh: 1 } : params;
      const r = await prsApi.dashboard(reqParams);
      setRows(r.data?.rows || []);
      setSyncInfo(r.data?.sync_info || null);
    } catch (e) {
      // Fallback to the legacy list endpoint if the combined one is unavailable
      // (e.g. backend rolled back). KPIs are computed client-side now so we
      // don't need to hit /api/kpis on the fallback path anymore.
      if (e?.response?.status === 404) {
        try {
          const a = await prsApi.list(params);
          setRows(a.data || []);
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Failed to load PRs");
        }
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load PRs");
      }
    } finally {
      setBusy(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const openPR = (id) => { setOpenId(id); setDrawerOpen(true); };

  const handleKpiClick = (key) => {
    if (focus === key) {
      setFocus("all");
      setFilters((f) => ({ ...f, ai_action: "All" }));
      return;
    }
    setFocus(key);
    if (key === "approve") setFilters((f) => ({ ...f, ai_action: "APPROVE" }));
    else if (key === "hold") setFilters((f) => ({ ...f, ai_action: "HOLD" }));
    else if (key === "all") setFilters((f) => ({ ...f, ai_action: "All" }));
  };

  // ---- Client-side filtering (Feb 2026) ----
  // The server now returns ALL PRs in scope (mode + approval + approver only).
  // purc_type / vdate / supplier / ai_action are applied here in-memory.
  // Filtering ~few hundred rows takes <1ms so keystrokes are instant and no
  // spinner is needed. Matches the pattern in Pipeline.jsx.
  const clientFilteredRows = useMemo(() => {
    let out = rows;
    if (filters.purc_type && filters.purc_type !== "All") {
      const pt = filters.purc_type;
      out = out.filter((r) => (r.purc_type || "") === pt);
    }
    if (filters.vdate) {
      // vdate on the row is an ISO datetime; compare by yyyy-mm-dd prefix.
      const day = filters.vdate;
      out = out.filter((r) => (r.vdate || "").slice(0, 10) === day);
    }
    if (filters.supplier) {
      const sl = filters.supplier.trim().toLowerCase();
      out = out.filter((r) => (r.supplier || "").toLowerCase().includes(sl));
    }
    return out;
  }, [rows, filters.purc_type, filters.vdate, filters.supplier]);

  // Unique supplier names from the *unfiltered* set — powers the <datalist>
  // autocomplete on the supplier input, so users can pick any supplier they
  // could possibly filter to (not just the ones currently narrowed down).
  const supplierSuggestions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.supplier && s.add(r.supplier));
    return Array.from(s).sort();
  }, [rows]);

  // KPIs are computed from the filtered rows but BEFORE the ai_action /
  // focus filter, so clicking a KPI card doesn't cause the counter itself
  // to change — matches server behaviour prior to this refactor.
  const computedKpi = useMemo(() => {
    const src = clientFilteredRows;
    let priceInc = 0, cheaper = 0, autoApprove = 0, hold = 0;
    for (const r of src) {
      if ((r.price_flag?.score || 0) > 0) priceInc += 1;
      if ((r.vendor_flag?.score || 0) > 0) cheaper += 1;
      if (r.ai_action === "APPROVE") autoApprove += 1;
      if (r.ai_action === "HOLD") hold += 1;
    }
    return {
      total: src.length,
      price_increase: priceInc,
      cheaper_vendor: cheaper,
      auto_approve: autoApprove,
      review: src.length - autoApprove - hold,
      hold,
    };
  }, [clientFilteredRows]);

  const displayRows = useMemo(() => {
    let base = clientFilteredRows;
    // ai_action filter (was server-side; now client-side).
    if (filters.ai_action && filters.ai_action !== "All") {
      const want = filters.ai_action.toUpperCase();
      base = base.filter((r) => r.ai_action === want);
    }
    // focus is KPI-card driven (highlights price_inc / cheaper_vendor buckets).
    if (focus === "price_inc") return base.filter((r) => r.price_flag?.score > 0);
    if (focus === "cheaper") return base.filter((r) => r.vendor_flag?.score > 0);
    return base;
  }, [clientFilteredRows, filters.ai_action, focus]);

  const focusLabels = {
    all: null,
    price_inc: "Price Increase only",
    cheaper: "Cheaper Vendor only",
    approve: "Auto-Approve only",
    hold: "Hold only",
  };


  // BroadcastChannel used to notify sibling Dashboard tabs the moment a PR is
  // approved/rejected/held, so they can drop it from their local table
  // without waiting for a natural refresh. Cleaned up on unmount.
  const decisionsChannelRef = useRef(null);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel("smart-approve-decisions");
    decisionsChannelRef.current = ch;
    ch.onmessage = (ev) => {
      const msg = ev.data;
      if (!msg || msg.type !== "pr-decided" || !Array.isArray(msg.voc_nos)) return;
      const drop = new Set(msg.voc_nos.map(Number));
      // Remove the just-decided rows from THIS tab's table so both tabs stay
      // in sync. KPI recomputes automatically from `computedKpi`.
      setRows((prev) => prev.filter((r) => !drop.has(Number(r.voc_no))));
    };
    return () => ch.close();
  }, []);

  const bulkAction = async (action) => {
    if (selected.length === 0) return toast.error("Select at least one PR");
    setBusy(true);
    // Optimistic UI: remove the affected rows from the local table immediately.
    const voc_nos = Array.from(new Set(selected.map((v) => Number(v))));
    const affected = new Set(voc_nos);
    const prevRows = rows;
    setRows((prev) => prev.filter((r) => !affected.has(Number(r.voc_no))));
    setSelected([]);

    const pastTense = (a) => ({
      approve: "approved",
      authorise: "authorised",
      reject: "rejected",
      hold: "put on hold",
    }[a] || a);
    const mappedAction = mode === "authorise" && action === "approve" ? "authorise" : action;
    const verb = mappedAction === "reject" ? "Rejecting" : mappedAction === "hold" ? "Holding" : mode === "authorise" ? "Authorising" : "Approving";

    // Progress toast — updated after each PR completes so users see a live
    // counter ("Approved 7/15 · 8 more…") instead of a blind spinner.
    const toastId = "bulk-progress";
    const total = voc_nos.length;
    toast.loading(`${verb} 0 / ${total} PRs…`, { id: toastId });

    const succeeded = [];
    const failed = [];
    // Concurrency 3 — small enough to avoid hammering MS SQL, big enough
    // to keep 15-30 PR batches under ~2-3s wall time.
    const CONCURRENCY = 3;
    const queue = [...voc_nos];
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const voc = queue.shift();
        if (voc == null) return;
        try {
          if (mode === "authorise") {
            await prsApi.authorise(voc, mappedAction, null);
          } else {
            await prsApi.decide(voc, mappedAction, null);
          }
          succeeded.push(voc);
        } catch (e) {
          // Sanitise the error text so end-users don't see raw `pymssql`
          // exception blobs (e.g. "20009 ... 59.98.165.33 ... No route to
          // host (113)"). Backend now returns HTTP 503 with a clean
          // "Database temporarily unavailable — please try again." message
          // on connectivity issues, so we surface that verbatim; other
          // errors keep their existing text. Never longer than 140 chars.
          const raw = e?.response?.data?.detail || e?.message || "failed";
          const clean = /pymssql|DB-Lib|adaptive server|net-lib/i.test(raw)
            ? "Database temporarily unavailable — please try again."
            : String(raw).slice(0, 140);
          failed.push({ voc_no: voc, err: clean });
        }
        toast.loading(`${verb} ${succeeded.length + failed.length} / ${total} PRs…`, { id: toastId });
      }
    });
    await Promise.all(workers);

    // Broadcast to sibling tabs so their tables drop the same rows.
    if (decisionsChannelRef.current && succeeded.length) {
      try {
        decisionsChannelRef.current.postMessage({ type: "pr-decided", voc_nos: succeeded, action: mappedAction });
      } catch {/* noop */}
    }

    // Final toast — dismiss the loading one, replace with summary.
    toast.dismiss(toastId);
    if (failed.length === 0) {
      toast.success(`${succeeded.length} PR${succeeded.length === 1 ? "" : "s"} ${pastTense(mappedAction)}`);
    } else if (succeeded.length === 0) {
      // Everything failed — put the rows back so the user can retry.
      setRows(prevRows);
      toast.error(`All ${total} PR${total === 1 ? "" : "s"} failed. First error: ${failed[0].err}`);
    } else {
      // Partial success — bring only the failed ones back into the list.
      const failedSet = new Set(failed.map((f) => f.voc_no));
      const backRows = prevRows.filter((r) => failedSet.has(Number(r.voc_no)));
      setRows((prev) => [...backRows, ...prev]);
      toast.warning(
        `${succeeded.length} ${pastTense(mappedAction)}, ${failed.length} failed. Failed PRs restored to the list. First error: ${failed[0].err}`,
        { duration: 6000 }
      );
    }
    // Reconcile KPIs (e.g. PRs promoted L1→L2). Non-blocking.
    load({ fresh: true });
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1600px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={18} />
            </div>
            <div className="min-w-0 hidden sm:block">
              <div className="text-lg sm:text-xl font-bold tracking-tight leading-none truncate">PR Approval Dashboard</div>
              <MirrorFreshness syncInfo={syncInfo} />
            </div>
            <div className="sm:hidden min-w-0">
              <div className="text-sm font-bold tracking-tight leading-none truncate">Approvals</div>
              <MirrorFreshness syncInfo={syncInfo} compact />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-4 flex-shrink-0">
            <PrSearchBox onFound={(vn) => openPR(vn)} variant="header" />
            <WorkspaceSwitcher role={role} />
            {(canAuthorise && canApprove) && (
              <div className="hidden sm:flex bg-white/10 rounded-lg p-0.5" data-testid="mode-toggle">
                <button
                  data-testid="mode-authorise"
                  onClick={() => setMode("authorise")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${mode === "authorise" ? "bg-white text-cyan-800" : "text-cyan-50 hover:bg-white/10"}`}
                >
                  <Stamp size={12} /> Authorise
                </button>
                <button
                  data-testid="mode-approve"
                  onClick={() => setMode("approve")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${mode === "approve" ? "bg-white text-cyan-800" : "text-cyan-50 hover:bg-white/10"}`}
                >
                  <BadgeCheck size={12} /> Approve
                </button>
                <button
                  data-testid="mode-held"
                  onClick={() => setMode("held")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center gap-1 ${mode === "held" ? "bg-white text-cyan-800" : "text-cyan-50 hover:bg-white/10"}`}
                  title="PRs you've put on Hold — re-surface or un-hold"
                >
                  <Pause size={12} /> Held
                </button>
              </div>
            )}
            {role === "Admin" && (
              <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="admin-link" className="text-white hover:bg-white/10 px-2 sm:px-3">
                <Settings size={16} className="sm:mr-1" />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            )}
            <PendingBadge />
            <UserMenu tone="dark" />
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-4 sm:space-y-5">
        <SavingsBanner approver={filters.approver} mode={mode} onPrClick={openPR} />

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiCard testId="kpi-total" label="Total PRs" value={computedKpi.total} accent="purple" icon={FileText}
                   onClick={() => handleKpiClick("all")} active={focus === "all"} />
          <KpiCard testId="kpi-price-inc" label="Price Increase" value={computedKpi.price_increase} accent="amber" icon={TrendingUp}
                   onClick={() => handleKpiClick("price_inc")} active={focus === "price_inc"} />
          <KpiCard testId="kpi-cheaper" label="Cheaper Vendor" value={computedKpi.cheaper_vendor} accent="orange" icon={Boxes}
                   onClick={() => handleKpiClick("cheaper")} active={focus === "cheaper"} />
          <KpiCard testId="kpi-approve" label="Auto-Approve" value={computedKpi.auto_approve} accent="green" icon={CheckCircle2}
                   onClick={() => handleKpiClick("approve")} active={focus === "approve"} />
          <KpiCard testId="kpi-hold" label="Hold" value={computedKpi.hold} accent="red" icon={AlertOctagon}
                   onClick={() => handleKpiClick("hold")} active={focus === "hold"} />
        </div>

        <FilterBar
          filters={filters}
          setFilters={setFilters}
          approvers={approvers}
          showApproverFilter={role === "Admin"}
          supplierSuggestions={supplierSuggestions}
        />

        {/* Action bar */}
        <div className="bg-white border border-slate-200 rounded-sm px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap min-w-0">
            <span><span className="font-semibold text-slate-900">{displayRows.length}</span> PR(s)</span>
            {focusLabels[focus] && (
              <button
                onClick={() => handleKpiClick(focus)}
                data-testid="clear-focus"
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800 hover:bg-purple-200"
              >
                {focusLabels[focus]}
                <XCircle size={12} />
              </button>
            )}
            {selected.length > 0 && (
              <span>· <span className="font-semibold text-cyan-800">{selected.length}</span> selected</span>
            )}
            <Button size="sm" variant="outline" onClick={() => load({ fresh: true })} disabled={busy} data-testid="refresh-btn" className="ml-auto sm:ml-2 h-8">
              <RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
          {selected.length > 0 && (
            <div className="grid grid-cols-3 sm:flex sm:items-center gap-2">
              <BulkConfirmButton action="approve" count={selected.length} onConfirm={bulkAction}
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
                testId="bulk-approve" icon={<CheckCircle2 size={14} className="mr-1" />} label="Approve" />
              <BulkConfirmButton action="hold" count={selected.length} onConfirm={bulkAction}
                className="border-yellow-400 text-yellow-900 hover:bg-yellow-50 w-full sm:w-auto" variant="outline"
                testId="bulk-hold" icon={<PauseCircle size={14} className="mr-1" />} label="Hold" />
              <BulkConfirmButton action="reject" count={selected.length} onConfirm={bulkAction}
                className="bg-rose-600 hover:bg-rose-700 text-white w-full sm:w-auto"
                testId="bulk-reject" icon={<XCircle size={14} className="mr-1" />} label="Reject" />
            </div>
          )}
        </div>

        <PRTable
          rows={displayRows}
          selected={selected}
          setSelected={setSelected}
          onOpen={openPR}
          busy={busy}
        />

        {/* Legend */}
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 text-xs text-slate-600 flex flex-wrap gap-x-6 gap-y-2">
          <span className="font-semibold text-slate-800">Approval Logic:</span>
          <span>Risk Score = Price Score + Vendor Score</span>
          <span><b>0–20</b> → AUTO-APPROVE</span>
          <span><b>21–60</b> → REVIEW</span>
          <span><b>&gt;60</b> → HOLD</span>
          <span className="ml-auto flex items-center gap-1 text-slate-500">
            <Sparkles size={12} /> Claude Sonnet 4.5 reasoning available per PR
          </span>
        </div>
      </main>

      <PRDetailDrawer
        vocNo={openId}
        open={drawerOpen}
        mode={mode}
        onClose={() => setDrawerOpen(false)}
        onDecided={(vn) => {
          // Optimistic: remove the single PR from the local list immediately.
          // Backend already patched the mirror in-place; load() reconciles KPIs.
          if (vn != null) {
            const target = Number(vn);
            setRows((prev) => prev.filter((r) => Number(r.voc_no) !== target));
            // Broadcast to sibling tabs so they drop the row too.
            if (decisionsChannelRef.current) {
              try { decisionsChannelRef.current.postMessage({ type: "pr-decided", voc_nos: [target] }); }
              catch {/* noop */}
            }
          }
          setSelected([]);
          load({ fresh: true });
        }}
      />
    </div>
  );
}

function BulkConfirmButton({ action, count, onConfirm, className, testId, icon, label, variant }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant={variant} className={className} data-testid={testId}>
          {icon}{label} ({count})
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm bulk {action}</AlertDialogTitle>
          <AlertDialogDescription>
            This will write <b>{action.toUpperCase()}</b> to {count} PR(s) in the MS SQL database (PoRequest &amp; PoRequesti).
            This action cannot be undone from this UI.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testId}-confirm`}
            onClick={() => onConfirm(action)}
            className={className}
          >
            Confirm {action.toUpperCase()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
